// components/three/npc/ServerNPCBridge.jsx
'use client'

import { useCallback, useEffect, useRef } from 'react'
import { birdPose } from '../../../lib/world/birdPose'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useNPCInstancedAnimations } from './NPCInstancedAnimationProvider'
import { useObstacles } from '../obstacles/ObstacleProvider'
import { useWithFloors } from '../terrain/withFloors'
import { inCar, liftsNow } from '../../../lib/world/liftClient'
import { WALLS } from '../../../lib/obstacles/walls'
import { useTerrainHeight } from '../terrain/TerrainHeightProvider'
import { useSimConnection } from '../../../hooks/useSimConnection'
import useGameStore from '../../../stores/gameStore'
import { clipForAction, IDLE_CLIP } from '../../../lib/world/actionClips'
import { seatFor } from '../../../lib/world/seats'
import { restingLid } from '../../../lib/world/blink'
import { moodState } from '../../../lib/world/mood'
import { lookOfCarrying, lookKey } from '../../../lib/things/looks'
import { BirdHit } from './BirdHit'

// Interpolation settings
const LERP_POSITION = 0.15  // Position smoothing (higher = faster)
const LERP_ROTATION = 0.12  // Rotation smoothing

// One-shot gestures: sim gesture name → clip in bird.glb. A name whose
// clip is not in the model simply never plays, so the sim can gesture
// before the animation exists. Duration is the override window; when the
// real clip lands, set this to its length.
const GESTURE_CLIP = { headTilt: 'HeadTilt' }
const GESTURE_MS = 1400

// Ground-snap: the server is XZ-only and sends pos.y = 0, so without this the
// bird sinks under any raised terrain. We sample the terrain height field at
// his XZ and drive his rendered Y toward it. GROUND_LERP is an EMA that keeps
// slope transitions smooth; GROUND_FOOT_OFFSET is the model origin-to-feet
// delta (bird origin sits at its feet, so ~0 — tune if his feet float/sink).
const GROUND_LERP = 0.3
const GROUND_FOOT_OFFSET = 0

// Night head-torch — a warm spotlight mounted at Victor's head, aimed FORWARD
// along his facing (like a head torch), so it lights the ground ahead of him
// and he's easy to follow in the dark. Off by day, ramps in with
// moodState.nightness. No shadow (soft glow + skips a per-frame shadow pass).
const TORCH_HEAD_HEIGHT = 0.7    // mount height above his origin (his "head")
const TORCH_REACH = 6            // how far ahead the beam is aimed
const TORCH_AIM_DROP = 1.2       // how far below head height the beam lands (tilts it down)
const TORCH_MAX_INTENSITY = 14   // at full night (moodState.nightness = 1)
const TORCH_ANGLE = 0.6          // cone half-angle (radians)
const TORCH_PENUMBRA = 0.5       // soft edge
const TORCH_DISTANCE = 20
const TORCH_DECAY = 1.1
const TORCH_COLOR = '#ffdca8'

// The newest thing in his bag (world/Bag.js on the sim, which says which:
// his beak merged into his bag on 3 Nov). Nothing is drawn at his beak.
// Sam, 3 Nov: "I think we don't have it physically in his beak - it looks
// weird I think". It goes on birdPose as its look (lib/things/looks.js) for
// the bubble over his head (ThoughtBubble.jsx), which shows a visitor who
// comes near that there is something to open. Every delta repeats it, so
// it is compared by its key and only a different thing is written; a delta
// without it leaves it as it was. Something he carries that the site has no
// picture for still counts as something: the bubble shows, with nothing in
// it.
const NO_PICTURE = Object.freeze({})
function noteCarrying(c) {
  const look = c ? lookOfCarrying(c) || NO_PICTURE : null
  const key = c ? lookKey(look) || 'something' : ''
  if (key === birdPose.carryingKey) return
  birdPose.carryingKey = key
  birdPose.carrying = look
}

// NPC colors (soft, distinctive colors)
const NPC_COLORS = {
  npc_victor: [0.95, 0.75, 0.3], // Golden yellow — current bird
  npc_pip:    [0.95, 0.75, 0.3], // Back-compat for any old snapshots
  npc_bean:   [0.4, 0.8, 0.5],   // Soft green
  npc_mochi:  [0.7, 0.5, 0.9],   // Lavender purple
  npc_taro:   [0.3, 0.7, 0.9],   // Sky blue
}
const DEFAULT_COLOR = [0.9, 0.9, 0.9]  // Light grey

/**
 * Bridge between sim-server world state and the GPU-instanced animation renderer.
 * Connects to the sim-server WebSocket and updates NPC instances based on server state.
 *
 * @param {string} wsUrl - WebSocket URL (optional, defaults to ws://localhost:3001)
 */
export function ServerNPCBridge({ wsUrl }) {
  const instanced = useNPCInstancedAnimations()

  // NPC state with interpolation targets
  // id -> { currentPos, targetPos, currentRotY, targetRotY, action }
  const npcStateRef = useRef(new Map())

  // Incremental deltas update the interpolation targets. Straight off the
  // socket into the refs: this used to be an effect on a piece of React
  // state, which meant a render of this component (and a reconcile of the
  // torch below) ten times a second for no visible change.
  const onDelta = useCallback((delta) => {
    if (!delta?.npcs) return
    for (const npc of delta.npcs) {
      const state = npcStateRef.current.get(npc.id)
      if (!state) continue
      // Update target position (will be lerped in useFrame)
      if (npc.pos) {
        state.targetPos.set(npc.pos.x, npc.pos.y, npc.pos.z)
      }
      if (npc.rotY !== undefined) {
        state.targetRotY = npc.rotY
      }
      if (npc.action) {
        state.action = npc.action
      }
      // Which place, for the places that share a verb (a granted place's
      // clip and seat are its own)
      if ('actionTarget' in npc) state.actionTargetId = npc.actionTarget?.id || null
      // the eyelids follow how much energy he has: the mind's, while one is connected, else the body's
      if (typeof npc.mind?.energy === 'number') state.arousal = npc.mind.energy
      else if (npc.mood?.arousal !== undefined) {
        state.arousal = npc.mood.arousal
      }
      if (npc.id === 'npc_victor' && 'carrying' in npc) noteCarrying(npc.carrying)
      // A gesture is a one-shot layered over the action: the head tilt
      // when something catches him off guard. seq tells a new one from
      // the echo of the last in every delta; the clip only plays if the
      // model actually carries it, so this whole path is dormant until
      // the animation lands in bird.glb.
      if (npc.gesture?.seq && npc.gesture.seq !== state.gestureSeq) {
        state.gestureSeq = npc.gesture.seq
        const clip = GESTURE_CLIP[npc.gesture.name]
        if (clip && instanced?.hasClip?.(clip)) {
          state.gestureClip = clip
          state.gestureUntil = performance.now() + GESTURE_MS
        }
      }
    }
  }, [instanced])

  const { connectionState, worldSnapshot, connect, disconnect, error, sendMessage } = useSimConnection(wsUrl, { onDelta })
  const { obstacles } = useObstacles()
  const terrainField = useWithFloors(useTerrainHeight())

  // Update store with connection status
  const setConnectionStatus = useGameStore((s) => s.setConnectionStatus)

  // Expose the WS sender for walker signals (FP whistle + marks). Cleared on
  // unmount so a dead socket can't be written to.
  const setSignalSender = useGameStore((s) => s.setSignalSender)
  useEffect(() => {
    setSignalSender(sendMessage)
    return () => setSignalSender(null)
  }, [sendMessage, setSignalSender])

  // Send obstacle AABBs to sim-server once on first ready snapshot. The
  // viewer parses obstacles.glb client-side already; rather than duplicate
  // the GLB-parsing logic on the server, we ship the resulting world-space
  // AABBs over the WebSocket. Sim-server uses them for slide-collision in
  // moveToward / sleep walks.
  const obstaclesSentRef = useRef(false)
  useEffect(() => {
    // Re-send on every (re)connection. The sim holds obstacles only in memory,
    // so after a sim restart — or if a first send is dropped on a flaky
    // connection — it has none (collision + pathfinding silently off) until we
    // ship them again. Reset the "sent" latch whenever we're not connected so
    // the next stable connection re-delivers them.
    if (connectionState !== 'connected') {
      obstaclesSentRef.current = false
      return
    }
    if (obstaclesSentRef.current) return
    if (!obstacles || obstacles.length === 0) return
    // Ship ORIENTED boxes, not enclosing AABBs: a rotated building's AABB
    // sticks out past its faces, so the bird would collide with empty space
    // on the diagonal. ca/sa carry the Y-rotation from the box's axisX basis.
    const aabbs = obstacles.map((o) => {
      let ca = o.axisX.x, sa = -o.axisX.z
      const len = Math.hypot(ca, sa) || 1
      return {
        name: o.name,
        cx: o.center.x, cz: o.center.z,
        hx: o.halfExtents.x, hz: o.halfExtents.z,
        ca: ca / len, sa: sa / len,
      }
    })
    // with the version of these walls, so a town still holding an older set
    // (an old tab got in first after a deploy) takes these in its place
    const ok = sendMessage({ type: 'client_obstacles', aabbs, version: WALLS.version })
    if (ok) {
      console.log(`[ServerNPCBridge] Sent ${aabbs.length} obstacle AABBs to sim-server`)
      obstaclesSentRef.current = true
    }
  }, [connectionState, obstacles, sendMessage])

  // Track allocated NPC slots
  const allocatedIdsRef = useRef(new Set())

  // Reusable objects to avoid GC
  const tempEuler = useRef(new THREE.Euler())
  const tempQuaternion = useRef(new THREE.Quaternion())
  const seatPos = useRef(new THREE.Vector3())
  const torchFwd = useRef(new THREE.Vector3())

  // Night head-torch that follows Victor (see constants above). The spotlight
  // needs a target Object3D in the scene graph to aim at; we move both each frame.
  const torchRef = useRef()
  const torchTargetRef = useRef()

  // Sync connection status to store
  useEffect(() => {
    setConnectionStatus(connectionState)
  }, [connectionState, setConnectionStatus])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect()
    return () => disconnect()
  }, [connect, disconnect])

  // Allocate/deallocate slots based on server snapshot
  useEffect(() => {
    if (!worldSnapshot?.npcs || !instanced?.allocateSlot) return

    const serverIds = new Set(worldSnapshot.npcs.map(npc => npc.id))

    // Allocate slots for new NPCs
    worldSnapshot.npcs.forEach((npc) => {
      if (!allocatedIdsRef.current.has(npc.id)) {
        const slot = instanced.allocateSlot(npc.id)
        if (slot !== -1) {
          allocatedIdsRef.current.add(npc.id)
          if (npc.id === 'npc_victor' && 'carrying' in npc) noteCarrying(npc.carrying)
          // Initialize state with both current and target at same position
          const posX = npc.pos?.x ?? 0
          const posY = npc.pos?.y ?? 0
          const posZ = npc.pos?.z ?? 0
          npcStateRef.current.set(npc.id, {
            currentPos: new THREE.Vector3(posX, posY, posZ),
            targetPos: new THREE.Vector3(posX, posY, posZ),
            currentRotY: npc.rotY ?? 0,
            targetRotY: npc.rotY ?? 0,
            action: npc.action,
            actionTargetId: npc.actionTarget?.id || null,
            // Carried for the eyelids: how keyed-up he is decides how
            // heavy they sit between blinks. Same dial the HUD draws as
            // Calm, and the one weather now moves.
            arousal: typeof npc.mind?.energy === 'number' ? npc.mind.energy : (npc.mood?.arousal ?? 0),
          })

          // Set NPC color
          if (instanced.setInstanceColor) {
            const color = NPC_COLORS[npc.id] || DEFAULT_COLOR
            instanced.setInstanceColor(npc.id, color[0], color[1], color[2])
          }

          console.log(`[ServerNPCBridge] ${npc.name} connected at (${posX.toFixed(1)}, ${posZ.toFixed(1)})`)
        }
      } else {
        // Update target position for existing NPC (snap on full snapshot)
        const state = npcStateRef.current.get(npc.id)
        if (state) {
          state.targetPos.set(npc.pos.x, npc.pos.y, npc.pos.z)
          state.targetRotY = npc.rotY
          state.action = npc.action
          state.actionTargetId = npc.actionTarget?.id || null
          // Snap current to target on full snapshot (reconnection)
          state.currentPos.copy(state.targetPos)
          state.currentRotY = state.targetRotY
        }
      }
    })

    // Release slots for NPCs that are no longer in server state
    allocatedIdsRef.current.forEach((id) => {
      if (!serverIds.has(id)) {
        instanced.releaseSlot(id)
        allocatedIdsRef.current.delete(id)
        npcStateRef.current.delete(id)
      }
    })
  }, [worldSnapshot, instanced])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (instanced?.releaseSlot) {
        allocatedIdsRef.current.forEach((id) => {
          instanced.releaseSlot(id)
        })
        allocatedIdsRef.current.clear()
        npcStateRef.current.clear()
      }
    }
  }, [instanced])

  // Update instanced renderer each frame with interpolation
  useFrame(() => {
    if (!instanced?.updateInstance) return

    npcStateRef.current.forEach((state, id) => {
      // Lerp position toward target: the sim's, or the seat he is sat on
      // (lib/world/seats.js), which the sim stands him in front of
      const seat = seatFor(state.action, state.actionTargetId)
      if (seat) seatPos.current.set(seat.x, 0, seat.z)
      state.currentPos.lerp(seat ? seatPos.current : state.targetPos, LERP_POSITION)

      // Ground-snap Y to the terrain surface. Server pos.y is always 0 (the sim
      // is XZ-only), so we own Y here entirely — overwrite whatever the lerp did
      // to it. state.groundY is an EMA so slope changes never pop. A raised
      // floor he walks onto (WALK_ in obstacles.glb: the tube carriage's,
      // and the pallets at its door) is ground too (useWithFloors).
      if (terrainField) {
        // in the monorail's lift he stands on its car, wherever it is
        const car = inCar(state.currentPos.x, state.currentPos.z) ? liftsNow().find((l) => inCar(state.currentPos.x, state.currentPos.z) === l.lf) : null
        state.inCar = !!car
        const g = (car ? car.y : terrainField.sample(state.currentPos.x, state.currentPos.z)) + GROUND_FOOT_OFFSET
        state.groundY = state.groundY === undefined || car ? g : state.groundY + (g - state.groundY) * GROUND_LERP
        // up onto a seat and back down off it at the same easing
        state.seatLift = (state.seatLift || 0) + ((seat?.lift || 0) - (state.seatLift || 0)) * GROUND_LERP
        state.currentPos.y = state.groundY + state.seatLift
      }

      // Lerp rotation (simple for Y-axis only)
      // Handle wrapping around PI/-PI
      let rotDiff = (seat ? seat.rotY : state.targetRotY) - state.currentRotY
      if (rotDiff > Math.PI) rotDiff -= Math.PI * 2
      if (rotDiff < -Math.PI) rotDiff += Math.PI * 2
      state.currentRotY += rotDiff * LERP_ROTATION

      // The Pino cam follows exactly this: the bird as drawn.
      if (id === 'npc_victor') {
        birdPose.x = state.currentPos.x
        birdPose.y = state.currentPos.y
        birdPose.z = state.currentPos.z
        birdPose.rotY = state.currentRotY
        birdPose.action = state.action || null
        birdPose.seen = true
      }

      // Convert rotY to quaternion
      tempEuler.current.set(0, state.currentRotY, 0)
      tempQuaternion.current.setFromEuler(tempEuler.current)

      // What he is doing decides what he looks like. This used to be a
      // moving-or-not flag, so everything that was not walking rendered as
      // the bind pose: he slept standing up with his arms out.
      // A live gesture briefly outranks the action's clip, then hands back.
      const gesturing = state.gestureUntil && performance.now() < state.gestureUntil
      if (state.gestureUntil && !gesturing) { state.gestureUntil = 0; state.gestureClip = null }
      instanced.updateInstance(
        id, state.currentPos, tempQuaternion.current,
        // stood in the lift's car his walk is still under way in the town
        // (it carries him), so he would walk on the spot: he stands
        gesturing ? state.gestureClip : state.inCar && Math.hypot(state.currentPos.x - state.targetPos.x, state.currentPos.z - state.targetPos.z) < 0.15 ? IDLE_CLIP : clipForAction(state.action, state.actionTargetId), undefined,
        // Where his lids sit before blinking: shut asleep, heavy when calm.
        restingLid(state.action, state.arousal),
      )
    })

    // Head-torch follows Victor, aimed forward along his facing, brightening
    // as night falls. The light stays PERMANENTLY visible and only its
    // intensity ramps (0 by day): toggling a light's visibility changes the
    // scene's light count, which invalidates the lighting state and forces a
    // pipeline recompile of every lit material in one frame. That was the
    // frame drop at every dusk and dawn. Intensity is just a uniform.
    const torch = torchRef.current
    const torchTarget = torchTargetRef.current
    if (torch && torchTarget) {
      const victor = npcStateRef.current.get('npc_victor')
      if (torch.target !== torchTarget) torch.target = torchTarget
      if (victor) {
        const p = victor.currentPos
        // Forward vector from his current facing (same Euler the model uses).
        tempEuler.current.set(0, victor.currentRotY, 0)
        torchFwd.current.set(0, 0, 1).applyEuler(tempEuler.current)
        torch.position.set(p.x, p.y + TORCH_HEAD_HEIGHT, p.z)
        torchTarget.position.set(
          p.x + torchFwd.current.x * TORCH_REACH,
          p.y + TORCH_HEAD_HEIGHT - TORCH_AIM_DROP,
          p.z + torchFwd.current.z * TORCH_REACH,
        )
      }
      torch.intensity = victor ? TORCH_MAX_INTENSITY * moodState.nightness : 0
    }
  })

  // Log connection errors (warn-level to avoid noisy red console spam during reconnection)
  useEffect(() => {
    if (error) {
      console.warn('[ServerNPCBridge] Connection error:', error)
    }
  }, [error])

  // Behaviour bridge — what it renders is Victor's night head-torch
  // (spotlight + its aim target), positioned each frame in useFrame above,
  // and the box a click on him lands on, which is not (BirdHit.jsx).
  return (
    <>
      <spotLight
        ref={torchRef}
        color={TORCH_COLOR}
        intensity={0}
        distance={TORCH_DISTANCE}
        angle={TORCH_ANGLE}
        penumbra={TORCH_PENUMBRA}
        decay={TORCH_DECAY}
      />
      <object3D ref={torchTargetRef} />
      <BirdHit />
    </>
  )
}
